import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { endOfMonth, format, isValid, parse, parseISO, startOfMonth } from "date-fns";
import { useTranslation } from "react-i18next";
import { Input } from "./input";

type Mode = "month" | "manual";

export interface MonthRangeToggleProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (value: string) => void;
  onEndDateChange: (value: string) => void;
}

interface MonthRange {
  start: string;
  end: string;
}

const MONTH_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const ISO_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const asMonthId = (iso: string) => iso.slice(0, 7);

const normalizeMonthId = (raw: string): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(/[/.]/g, "-");
  const candidate = normalized.slice(0, 7);
  return MONTH_PATTERN.test(candidate) ? candidate : null;
};

const formatDateForApi = (value?: string | null): string | null => {
  if (!value) return null;

  if (ISO_DAY_PATTERN.test(value)) {
    return value;
  }

  try {
    const parsedIso = parseISO(value);
    if (isValid(parsedIso)) {
      return format(parsedIso, "yyyy-MM-dd");
    }
  } catch {
    return null;
  }

  const parsedDay = parse(value, "yyyy-MM-dd", new Date());
  return isValid(parsedDay) ? format(parsedDay, "yyyy-MM-dd") : null;
};

const getRangeForMonth = (monthId: string): MonthRange => {
  if (!MONTH_PATTERN.test(monthId)) {
    const now = new Date();
    return {
      start: format(startOfMonth(now), "yyyy-MM-dd"),
      end: format(endOfMonth(now), "yyyy-MM-dd"),
    };
  }

  const [year, month] = monthId.split("-").map(Number);
  const base = new Date(year, month - 1, 1);
  return {
    start: format(startOfMonth(base), "yyyy-MM-dd"),
    end: format(endOfMonth(base), "yyyy-MM-dd"),
  };
};

const detectFullMonthId = (
  startDate: string,
  endDate: string,
): string | null => {
  if (!startDate || !endDate) return null;

  const startMonthId = asMonthId(startDate);
  const endMonthId = asMonthId(endDate);
  if (startMonthId !== endMonthId) return null;

  const expectedRange = getRangeForMonth(startMonthId);
  return startDate === expectedRange.start && endDate === expectedRange.end
    ? startMonthId
    : null;
};

export function MonthRangeToggle({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: MonthRangeToggleProps) {
  const { t } = useTranslation(["timeEntries"] as const);
  const fieldClass = "w-full min-w-0 space-y-1 md:col-span-1";
  const labelRowClass = "flex h-6 items-center justify-between";
  const controlWrapClass = "w-full min-w-0";
  const unifiedControlClass =
    "h-[46px] w-full box-border border-input bg-surface-primary text-content-primary leading-none";

  const detectMonth = useMemo(
    () => detectFullMonthId(startDate, endDate),
    [startDate, endDate],
  );

  const [mode, setMode] = useState<Mode>(detectMonth ? "month" : "manual");
  const [monthValue, setMonthValue] = useState<string>(
    detectMonth ??
      (startDate ? asMonthId(startDate) : format(new Date(), "yyyy-MM")),
  );
  const previousDetectMonthRef = useRef<string | null>(detectMonth);

  useEffect(() => {
    const previousDetectMonth = previousDetectMonthRef.current;
    if (detectMonth === previousDetectMonth) {
      return;
    }

    previousDetectMonthRef.current = detectMonth;
    setMode(detectMonth ? "month" : "manual");

    if (detectMonth) {
      setMonthValue(detectMonth);
    }
  }, [detectMonth]);

  const handleMonthChange = (value: string) => {
    const sanitized = value.replace(/[/.]/g, "-");
    setMonthValue(sanitized);

    const normalizedMonth = normalizeMonthId(sanitized);
    if (!normalizedMonth) {
      return;
    }

    const range = getRangeForMonth(normalizedMonth);
    onStartDateChange(range.start);
    onEndDateChange(range.end);
  };

  const switchToMonthMode = () => {
    const sourceMonth = startDate
      ? asMonthId(startDate)
      : monthValue || format(new Date(), "yyyy-MM");
    const normalizedMonth =
      normalizeMonthId(sourceMonth) ?? format(new Date(), "yyyy-MM");
    const range = getRangeForMonth(normalizedMonth);
    onStartDateChange(range.start);
    onEndDateChange(range.end);
    setMonthValue(normalizedMonth);
    setMode("month");
  };

  if (mode === "month") {
    return (
      <div className={fieldClass}>
        <div className={labelRowClass}>
          <span className="text-xs font-semibold uppercase text-content-muted">
            {t("timeEntries.filters.fields.month")}
          </span>
          <button
            type="button"
            className="text-xs leading-none text-primary hover:text-primary/80"
            onClick={() => setMode("manual")}
          >
            {t("timeEntries.filters.actions.useManualRange")}
          </button>
        </div>

        <div className={controlWrapClass}>
          <Input
            type="month"
            value={monthValue}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleMonthChange(event.target.value)
            }
            placeholder="YYYY-MM"
            inputMode="text"
            className={unifiedControlClass}
            aria-label={t("timeEntries.filters.fields.month")}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={fieldClass}>
        <div className={labelRowClass}>
          <span className="text-xs font-semibold uppercase text-content-muted">
            {t("timeEntries.filters.fields.startDate")}
          </span>
          <button
            type="button"
            className="text-xs leading-none text-primary hover:text-primary/80"
            onClick={switchToMonthMode}
          >
            {t("timeEntries.filters.actions.useMonth")}
          </button>
        </div>

        <div className={controlWrapClass}>
          <Input
            type="date"
            value={startDate}
            max={endDate}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onStartDateChange(
                formatDateForApi(event.target.value) ?? event.target.value,
              )
            }
            className={unifiedControlClass}
            aria-label={t("timeEntries.filters.fields.startDate")}
          />
        </div>
      </div>

      <div className={fieldClass}>
        <div className={labelRowClass}>
          <span className="text-xs font-semibold uppercase text-content-muted">
            {t("timeEntries.filters.fields.endDate")}
          </span>
          <span
            aria-hidden
            className="invisible text-xs leading-none text-primary"
          >
            {t("timeEntries.filters.actions.useMonth")}
          </span>
        </div>

        <div className={controlWrapClass}>
          <Input
            type="date"
            value={endDate}
            min={startDate}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              onEndDateChange(
                formatDateForApi(event.target.value) ?? event.target.value,
              )
            }
            className={unifiedControlClass}
            aria-label={t("timeEntries.filters.fields.endDate")}
          />
        </div>
      </div>
    </>
  );
}

export default MonthRangeToggle;
